import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  HostListener,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormControl, Validators, AbstractControl } from '@angular/forms';
import { ReplaySubject, Subject, of, Observable, BehaviorSubject } from 'rxjs';
import { debounceTime, startWith, switchMap, takeUntil, catchError } from 'rxjs/operators';
import { ProceduresService } from '../../services/procedures.service';

@Component({
  selector: 'app-procedures-manual-select',
  templateUrl: './procedures-manual-select.component.html',
  styleUrls: ['./procedures-manual-select.component.scss'],
})
export class ProceduresManualSelectComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public proceduresArray: FormArray;
  @Input() public type: 'APAC' | 'SADT' = 'APAC';
  @Input() public reset$: Observable<void>;

  public filteringCtrl = new FormControl();
  public filteredOptions = new ReplaySubject<{ description: string; value: any; disabled: boolean }[]>(1);
  public isSearching = false;
  public activeRowIndex: number | null = null;

  private type$ = new BehaviorSubject<'APAC' | 'SADT'>('APAC');
  private readonly destroy$ = new Subject<void>();
  private currentProcedures: any[] = [];

  @HostListener('document:click')
  public onDocumentClick(): void {
    this.closeDropdown();
  }

  constructor(private fb: FormBuilder, private proceduresService: ProceduresService, private cdr: ChangeDetectorRef) {}

  public ngOnChanges(changes: SimpleChanges): void {
    const typeChange = changes['type'];
    if (typeChange && !typeChange.firstChange) {
      this.type$.next(typeChange.currentValue);
      this.filteringCtrl.setValue('', { emitEvent: true });
    }
  }

  public ngOnInit(): void {
    this.type$.next(this.type);
    this.initializeFiltering();
    this.initializeResetListener();
    this.ensureInitialRow();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeFiltering(): void {
    this.filteringCtrl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        startWith(''),
        debounceTime(300),
        switchMap((search) => this.fetchProcedures(search)),
      )
      .subscribe((procedures) => {
        this.isSearching = false;
        this.currentProcedures = procedures || [];
        this.updateFilteredOptions();
      });
  }

  private updateFilteredOptions(): void {
    if (!this.currentProcedures) return;

    const currentlySelectedIds =
      this.proceduresArray?.controls.map((c) => c.get('id')?.value).filter((id) => !!id) || [];

    const formatted = this.currentProcedures.map((proc) => ({
      description: `${proc.code} - ${proc.description}`,
      value: proc,
      disabled: currentlySelectedIds.includes(proc.id),
    }));

    this.filteredOptions.next(formatted);
    this.cdr.markForCheck();
  }

  private fetchProcedures(search: string): Observable<any[]> {
    this.isSearching = true;
    this.cdr.markForCheck();

    const request$ =
      this.type$.getValue() === 'APAC'
        ? this.proceduresService.getAPACProcedures(search)
        : this.proceduresService.getSADTProcedures(search);

    return request$.pipe(
      catchError(() => {
        this.isSearching = false;
        return of([]);
      }),
    );
  }

  public onProcedureManualSelected(selectedOption: any, index: number): void {
    const group = this.proceduresArray.at(index) as FormGroup;

    group.patchValue({
      id: selectedOption.id,
      description: selectedOption.description,
      code: selectedOption.code,
      protocolId: null,
      type: this.type$.getValue(),
    });

    this.closeDropdown();
    this.ensureEmptyRowAtEnd();
    this.updateFilteredOptions();
  }

  public toggleDropdown(index: number, event: MouseEvent): void {
    event.stopPropagation();
    this.activeRowIndex = this.activeRowIndex === index ? null : index;
  }

  public closeDropdown(): void {
    this.activeRowIndex = null;
  }

  public addProcedure(): void {
    const hasEmptyManual = this.getManualControls().some((control) => !control.get('id')?.value);

    if (hasEmptyManual) {
      return;
    }

    this.proceduresArray.push(this.createProcedureFormGroup());
  }

  public removeProcedure(index: number): void {
    this.proceduresArray.removeAt(index);
    this.updateFilteredOptions();
  }

  public isLastManualItem(index: number): boolean {
    const manualControls = this.getManualControls();
    const lastEmptyControl = [...manualControls].reverse().find((c) => !c.get('id')?.value);

    return this.proceduresArray.at(index) === lastEmptyControl;
  }

  private createProcedureFormGroup(): FormGroup {
    return this.fb.group({
      id: [null, Validators.required],
      code: [null],
      description: [null],
      quantity: [1, [Validators.required, Validators.min(1)]],
      protocolId: [null],
      type: [null],
    });
  }

  private ensureInitialRow(): void {
    if (this.getManualControls().length === 0) {
      this.addProcedure();
    }
  }

  private ensureEmptyRowAtEnd(): void {
    const hasEmptyManual = this.getManualControls().some((control) => !control.get('id')?.value);
    if (!hasEmptyManual) {
      this.addProcedure();
    }
  }

  private getManualControls(): AbstractControl[] {
    return this.proceduresArray.controls.filter((c) => !c.get('protocolId')?.value);
  }

  private initializeResetListener(): void {
    this.reset$?.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.proceduresArray.clear();
      this.addProcedure();
      this.updateFilteredOptions();
      this.cdr.markForCheck();
    });
  }
}
